import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";

const AdminCrossService = {
    requestCreators: async () => {
        const result = await ApiService.getData("/v1/user/creator");
        if (Array.isArray(result)) return result;
        if (Array.isArray(result?.content)) return result.content;
        if (Array.isArray(result?.creators)) return result.creators;
        if (Array.isArray(result?.users)) return result.users;
        return [];
    },

    requestList: async (
        paginationModel,
        sort,
        setRows,
        setRowCount,
        keyword,
        state,
        language,
        creatorId,
        hasStory,
        tags,
        free
    ) => {
        let param = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
            keyword: keyword ? encodeURIComponent(keyword) : "",
        };

        if (state) {
            param.state = state;
        }

        // Language filter: all = no param, english/french = send param
        if (language && language !== "all") {
            param.language = language;
        }

        if (creatorId) {
            param.creatorId = creatorId;
        }

        if (hasStory === true || hasStory === false) {
            param.hasStory = hasStory;
        }

        if (free === true || free === "true") {
            param.free = "true";
        } else if (free === false || free === "false") {
            param.free = "false";
        }

        if (tags && Array.isArray(tags) && tags.length > 0) {
            const tagStrings = tags
                .map((tag) => {
                    if (typeof tag === "string") return tag;
                    return tag?.tag || String(tag);
                })
                .map((tag) => String(tag).toLowerCase().trim())
                .filter((tag) => tag.length > 0);
            if (tagStrings.length > 0) {
                param.tags = tagStrings.join(",");
            }
        }

        Logger.log(param);

        const result = await ApiService.getData("/v1/puzzle/cross-tpl", param);
        // Logger.log(result);

        setRows(result.content);
        setRowCount(result.page?.totalElements ?? 0);
    },

    requestTemplate: async (crossTplId) => {
        const result = await ApiService.getData(`/v1/puzzle/cross-tpl/${crossTplId}`).catch((e) => Logger.log(e));

        // Logger.log(result);
        return result;
    },

    applyTopic: async (crossTplId, selectedTopicId) => {
        let param = {
            crossTplId: crossTplId,
            selectedTopicId: selectedTopicId,
        };

        // Logger.log(param);
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/apply_topic`, param);

        // Logger.log(result);
        return result;
    },

    requestTopic: async (topicId) => {
        // Logger.log("requestTopic::" + topicId);
        const result = await ApiService.getData(`/v1/topic/${topicId}`).catch((e) => Logger.log(e));

        // Logger.log(result);
        return result;
    },

    deleteTemplate: async (crossTplId) => {
        const result = await ApiService.deleteData(`/v1/puzzle/cross-tpl/${crossTplId}`);
        // Logger.log(result);

        return result;
    },

    deleteTemplates: async (crossTplIds) => {
        const result = await ApiService.deleteData(`/v1/puzzle/cross-tpl`, { ids: crossTplIds });
        Logger.log(result);

        return result;
    },

    verifyFilledCoord: async () => {
        const result = await ApiService.getData("/v1/puzzle/cross-tpl/verify-filled-coord");
        return Array.isArray(result) ? result : [];
    },

    genWordNetworkForNull: async () => {
        const result = await ApiService.putData("/v1/puzzle/cross-tpl/gen-word-network/for-null");
        return result;
    },

    genPreImageForNull: async (force = false) => {
        const qs = force ? "?force=true" : "";
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/gen-pre-image/for-null${qs}`);
        return result;
    },

    genPdfForNull: async () => {
        const result = await ApiService.putData("/v1/puzzle/cross-tpl/gen-pdf/for-null");
        return result;
    },

    genPdfForNullAsync: async () => {
        const result = await ApiService.putData("/v1/puzzle/cross-tpl/gen-pdf/for-null/async");
        return result;
    },

    replaceAllPdfs: async (page = 0, batchSize = 20) => {
        const p = Number.isFinite(page) ? Math.max(0, Math.floor(page)) : 0;
        const b = Number.isFinite(batchSize) ? Math.max(1, Math.floor(batchSize)) : 20;
        const qs = new URLSearchParams({ page: String(p), batchSize: String(b) });
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/gen-pdf/replace-all?${qs}`);
        return result;
    },

    replaceAllPdfsAsync: async () => {
        const result = await ApiService.putData("/v1/puzzle/cross-tpl/gen-pdf/replace-all/async");
        return result;
    },

    genClusteredBubbles: async (page = 0, batchSize = 300, force = false) => {
        const p = Number.isFinite(page) ? Math.max(0, Math.floor(page)) : 0;
        const b = Number.isFinite(batchSize) ? Math.max(1, Math.floor(batchSize)) : 300;
        const qs = new URLSearchParams({
            page: String(p),
            batchSize: String(b),
            force: String(!!force),
        });
        const result = await ApiService.postData(`/v1/puzzle/cross-tpl/gen-clustered-bubbles?${qs}`, {});
        return result;
    },

    syncWordInUse: async () => {
        const result = await ApiService.putData("/v1/puzzle/cross-tpl/sync-word-in-use");
        return result;
    },

    rePublish: async (sinceIsoDate) => {
        const params = new URLSearchParams();
        if (sinceIsoDate) {
            params.set("since", sinceIsoDate);
        }
        const qs = params.toString();
        const url = qs ? `/v1/puzzle/cross-tpl/re-publish?${qs}` : "/v1/puzzle/cross-tpl/re-publish";
        const result = await ApiService.postData(url, {});
        return result;
    },

    createTemplate: async (templateData) => {
        const result = await ApiService.postData(`/v1/puzzle/cross-tpl`, templateData);
        Logger.log(result);

        return result;
    },

    /**
     * @param {string} crossTplId
     * @param {{ storyLanguages?: string[], skipImage?: boolean }|undefined} body - optional; omit or {} for server defaults
     */
    genStory: async (crossTplId, body) => {
        const payload =
            body && typeof body === "object" && Object.keys(body).length > 0 ? body : {};
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTplId}/gen-story`, payload);
        if (result && result.code === "FAILURE") {
            const errorMessage = result.message || "Failed to generate story";
            const err = new Error(errorMessage);
            err.response = result;
            throw err;
        }
        return result;
    },

    /**
     * Softer educational retry after Claude refusal.
     * @param {string} crossTplId
     * @param {{ storyLanguages?: string[], skipImage?: boolean }|undefined} body
     */
    genStorySafe: async (crossTplId, body) => {
        const payload =
            body && typeof body === "object" && Object.keys(body).length > 0 ? body : {};
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTplId}/gen-story-safe`, payload);
        if (result && result.code === "FAILURE") {
            const errorMessage = result.message || "Failed to generate story (safe retry)";
            const err = new Error(errorMessage);
            err.response = result;
            throw err;
        }
        return result;
    },

    genPreviewImage: async (crossTplId) => {
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTplId}/gen-pre-image`);
        if (result && result.code === "FAILURE") {
            const err = new Error(result.message || "Failed to regenerate preview");
            err.response = result;
            throw err;
        }
        if (result && result.error) {
            throw new Error(result.error);
        }
        return result;
    },

    publish: async (crossTplId, isFree) => {
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTplId}/publish`, {
            isFree: isFree === true,
            makeNow: true,
        });
        if (result && result.code === "FAILURE") {
            const err = new Error(result.message || "Publish failed");
            err.response = result;
            throw err;
        }
        return result;
    },
};

export default AdminCrossService;

